import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

// Create axios instance with auth interceptor
const adminApi = axios.create({
  baseURL: ADMIN_API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth header to requests
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export const adminService = {
  // Authentication
  login: (username, password) => 
    adminApi.post('/login', { username, password }),
  
  // Dashboard
  getStats: () => adminApi.get('/stats'),
  getDownloadExportStats: () => adminApi.get('/stats/downloads-exports'),
  
  // Charts
  getUsersChart: (days = 30) => adminApi.get(`/charts/users?days=${days}`),
  getSessionsChart: (days = 30) => adminApi.get(`/charts/sessions?days=${days}`),
  getDownloadsChart: (days = 30) => adminApi.get(`/charts/downloads?days=${days}`),
  
  // Country Statistics
  getUsersByCountry: () => adminApi.get('/countries'),
  getAvailableCountries: () => adminApi.get('/countries/list'),
  
  // Error Logs
  getErrors: (params = {}) => adminApi.get('/errors', { params }),
  resolveError: (errorId, notes = '') => 
    adminApi.post(`/errors/${errorId}/resolve`, null, { params: { notes } }),
  deleteError: (errorId) => adminApi.delete(`/errors/${errorId}`),
  
  // API Health
  getHealth: () => adminApi.get('/health'),
  getApiHealth: async () => {
    try {
      const response = await adminApi.get('/health');
      // Transform array response to object format expected by frontend
      const services = {};
      let overallStatus = 'healthy';
      
      if (Array.isArray(response.data)) {
        response.data.forEach(item => {
          const key = item.service_name?.toLowerCase().replace(/[^a-z0-9]/g, '_') || 'unknown';
          services[key] = item.status;
          if (item.status === 'down') overallStatus = 'down';
          else if (item.status === 'degraded' && overallStatus !== 'down') overallStatus = 'degraded';
        });
        return { data: { status: overallStatus, services, raw: response.data } };
      }
      return response;
    } catch (error) {
      return { data: { status: 'unknown', services: {} } };
    }
  },
  
  // Profiles (original)
  getProfiles: (params = {}) => adminApi.get('/profiles', { params }),
  deleteProfile: (profileId) => adminApi.delete(`/profiles/${profileId}`),
  
  // Profiles (filtered - new)
  getProfilesFiltered: (params = {}) => adminApi.get('/profiles/filtered', { params }),
  exportProfiles: (params = {}) => adminApi.get('/profiles/export', { params }),
  
  // Sessions
  getSessions: (params = {}) => adminApi.get('/sessions', { params }),
  getSessionsWithSentiment: (params = {}) => adminApi.get('/sessions/with-sentiment', { params }),
  getSessionSentiment: (sessionId) => adminApi.get(`/sessions/${sessionId}/sentiment`),
  getSessionAISummary: (sessionId) => adminApi.get(`/sessions/${sessionId}/ai-summary`),
  deleteSession: (sessionId) => adminApi.delete(`/sessions/${sessionId}`),
  
  // Sentiment Analysis
  getSentimentStats: () => adminApi.get('/sentiment/stats'),
  
  // AI Query for Sessions
  querySessionsAI: (data) => adminApi.post('/sessions/query', data),
  
  // Events
  getEvents: (limit = 100) => adminApi.get(`/events?limit=${limit}`),
  cleanupEvents: (days = 90) => adminApi.post(`/events/cleanup?days=${days}`),
  
  // Leads Management
  getLeadsStats: () => adminApi.get('/leads/stats'),
  getLeadsChart: (days = 14) => adminApi.get(`/leads/chart?days=${days}`),
  getLeadsList: (params = {}) => adminApi.get('/leads/list', { params }),
  exportLeads: (params = {}) => adminApi.get('/leads/export', { params }),
  getLeadsCountries: () => adminApi.get('/leads/countries'),
  getDownloadStats: () => adminApi.get('/leads/downloads'),
};

export default adminService;
